import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calendar, 
  Clock, 
  ArrowLeft,
  ArrowRight,
  Tag,
  Phone,
  ChevronRight,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  Mail,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead, generateBlogSchema, breadcrumbSchema } from "@/components/SEOHead";
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

// Default blog images
const defaultImages = {
  "Case Studies": "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop",
  "Guides": "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop",
  "Education": "https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=1200&h=600&fit=crop",
  "News": "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200&h=600&fit=crop",
  "default": "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1200&h=600&fit=crop"
};

const getImageUrl = (post) => {
  return post?.image_url || defaultImages[post?.category] || defaultImages.default;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API}/blog/posts/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError("not_found");
        } else {
          throw new Error("Failed to fetch post");
        }
        return;
      }
      
      const data = await response.json();
      setPost(data.post);
      setRelatedPosts(data.related_posts || []);
    } catch (err) {
      console.error("Error fetching post:", err);
      setError("error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error === "not_found" || !post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h1>
          <p className="text-slate-600 mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareTitle = post.title;

  const handleShare = (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const postUrl = `https://monitoring.nationalbusinessregister.co.uk/blog/${post?.slug}`;

  return (
    <div className="min-h-screen bg-white">
      {post && (
        <SEOHead
          title={post.title}
          description={post.excerpt || post.title}
          path={`/blog/${post.slug}`}
          type="article"
          schema={{
            "@context": "https://schema.org",
            "@graph": [
              generateBlogSchema(post, postUrl),
              breadcrumbSchema([
                { name: "Home", url: "/landing" },
                { name: "Blog", url: "/blog" },
                { name: post.title }
              ])
            ]
          }}
        />
      )}
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/landing" className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-8 sm:h-10 w-auto" />
              <div className="hidden sm:block">
                <span className="font-heading font-black text-lg text-slate-900">NBR</span>
                <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/landing#methodology" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Features</Link>
              <Link to="/landing#pricing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Pricing</Link>
              <Link to="/blog" className="text-sm text-teal-600 font-medium">Blog</Link>
              <a href="tel:08000699090" className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1 transition-colors">
                <Phone className="w-4 h-4" />
                0800 069 9090
              </a>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/register-trademarks">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 sm:px-4">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <section className="pt-24 sm:pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
            <Link to="/landing" className="hover:text-teal-600">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-teal-600">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 truncate max-w-[200px]">{post.title}</span>
          </div>

          {/* Category & Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm font-medium text-teal-600 bg-teal-50 px-3 py-1 rounded-full">
              {post.category}
            </span>
            <div className="flex items-center gap-1 text-sm text-slate-500">
              <Calendar className="w-4 h-4" />
              {formatDate(post.date)}
            </div>
            <div className="flex items-center gap-1 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              {post.reading_time || post.readingTime} min read
            </div>
          </div>

          {/* Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl md:text-4xl font-heading font-black text-slate-900 mb-6 leading-tight"
          >
            {post.title}
          </motion.h1>

          {/* Author */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
              <span className="text-teal-600 font-bold text-sm">NBR</span>
            </div>
            <div>
              <p className="font-medium text-slate-900">{post.author}</p>
              <p className="text-sm text-slate-500">National Business Register</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl overflow-hidden"
          >
            <img 
              src={getImageUrl(post)} 
              alt={post.title}
              className="w-full h-64 sm:h-80 md:h-96 object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_200px] gap-8">
            {/* Main Content */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-slate prose-lg max-w-none
                prose-headings:font-heading prose-headings:font-bold prose-headings:text-slate-900
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-slate-600 prose-p:leading-relaxed
                prose-li:text-slate-600
                prose-strong:text-slate-900
                prose-a:text-teal-600 prose-a:no-underline hover:prose-a:underline"
            >
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </motion.article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                {/* Share */}
                <div className="mb-8">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Share2 className="w-4 h-4" />
                    Share Article
                  </h4>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:text-teal-600 transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:text-teal-600 transition-colors"
                    >
                      <Twitter className="w-4 h-4" />
                      Twitter
                    </button>
                    <button
                      onClick={() => handleShare('email')}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:text-teal-600 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, i) => (
                      <span 
                        key={i}
                        className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>

          {/* Mobile Share & Tags */}
          <div className="lg:hidden mt-8 pt-8 border-t border-slate-200">
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((tag, i) => (
                <span 
                  key={i}
                  className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-slate-900">Share:</span>
              <button onClick={() => handleShare('linkedin')} className="text-slate-500 hover:text-teal-600">
                <Linkedin className="w-5 h-5" />
              </button>
              <button onClick={() => handleShare('twitter')} className="text-slate-500 hover:text-teal-600">
                <Twitter className="w-5 h-5" />
              </button>
              <button onClick={() => handleShare('email')} className="text-slate-500 hover:text-teal-600">
                <Mail className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Blog */}
      <section className="px-4 sm:px-6 lg:px-8 py-8 bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Articles
          </Link>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all"
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={getImageUrl(relatedPost)} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded">
                      {relatedPost.category}
                    </span>
                    <h3 className="font-bold text-slate-900 mt-3 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {formatDate(relatedPost.date)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-white mb-4">
            Need Help Protecting Your Trade Mark?
          </h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Our experts at National Business Register have over 40 years of experience 
            helping businesses protect their intellectual property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register-trademarks">
              <Button className="bg-white text-teal-700 hover:bg-slate-100 px-8 py-6">
                Start Monitoring Today
              </Button>
            </Link>
            <a href="tel:08000699090">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6">
                <Phone className="w-4 h-4 mr-2" />
                Call 0800 069 9090
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-10 w-auto brightness-0 invert" />
              <div>
                <span className="font-heading font-black text-lg text-white">NBR</span>
                <span className="text-xs text-teal-400 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm text-center">
              © {new Date().getFullYear()} National Business Register Group Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
